import type { PublishReplyMessageClientsVariables } from '@ms/yammer-graphql';
import { getItem, getKeys, removeItem, setItemOrThrow } from '@ms/yammer-libs-local-storage';
import {
  getItem as getSessionItem,
  getKeys as getSessionKeys,
  removeItem as removeSessionItem,
  setItemOrThrow as setSessionItemOrThrow,
} from '@ms/yammer-session-storage';
import { reportError } from '@ms/yammer-telemetry';

import type { Message, Publisher, Thread, User, YammerState } from '../../state/types';
import type { ReplyPublisherPostedContext } from '../publishReplyMessage/reportReplyPublisherPosted';
import { getCurrentNetworkId, getCurrentSessionUserField } from '../session/selectors';
import { getCurrentUserFieldThrows } from '../users/selectors';

export const replacementContentRetentionMilliseconds: number = 24 * 60 * 60 * 1000;
const storagePrefix = 'facilitator-reply-replacement:';
const expiryTimers = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<() => void>();

interface ReplacementContext {
  readonly sourceMessageId: Message['id'];
  readonly threadId: Thread['id'];
  readonly parentMessageId: Message['id'];
  readonly publisherId: Publisher['id'];
}

export interface PublishingReplacement extends ReplacementContext {
  readonly stage: 'publishing';
  readonly createdAt: number;
  readonly clientMutationId: string;
  readonly variables: PublishReplyMessageClientsVariables & { readonly clientMutationId: string };
  readonly senderId: User['id'];
  readonly telemetryContext: ReplyPublisherPostedContext;
}

export interface PublishedReplacement extends ReplacementContext {
  readonly stage: 'cleanup';
  readonly publishedMessageId: Message['id'];
  readonly clientMutationId?: string;
}

export interface UnconfirmedReplacement extends ReplacementContext {
  readonly stage: 'reconciliation';
  readonly clientMutationId: string;
}

type StoredPublishingReplacement = Omit<PublishingReplacement, 'variables' | 'senderId' | 'telemetryContext'>;

export type ModeratedEventAgentReplyReplacement = PublishingReplacement | PublishedReplacement | UnconfirmedReplacement;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

const isOptionalString = (value: unknown): boolean => value === undefined || typeof value === 'string';

const isOptionalStringArray = (value: unknown): boolean =>
  value === undefined || (Array.isArray(value) && value.every((item) => typeof item === 'string'));

const hasPublishingRequest = (
  value: Record<string, unknown>
): value is PublishingReplacement & Record<string, unknown> => {
  const variables = value.variables;

  return (
    value.stage === 'publishing' &&
    hasReplacementContext(value) &&
    typeof value.createdAt === 'number' &&
    typeof value.clientMutationId === 'string' &&
    typeof value.senderId === 'string' &&
    isRecord(variables) &&
    typeof variables.replyToMessageMutationId === 'string' &&
    typeof variables.serializedContentState === 'string' &&
    typeof variables.clientMutationId === 'string' &&
    typeof variables.isSecondLevelReply === 'boolean' &&
    typeof variables.isModeratorMessage === 'boolean' &&
    typeof variables.isAnonymousMessage === 'boolean' &&
    typeof variables.isPrivateReply === 'boolean' &&
    isOptionalString(variables.onBehalfOfSenderId) &&
    isOptionalString(variables.sharedMessageId) &&
    isOptionalStringArray(variables.notifiedUserIds) &&
    isOptionalStringArray(variables.attachmentIds) &&
    hasTelemetryContext(value.telemetryContext)
  );
};

const hasReplacementContext = (value: unknown): value is ReplacementContext & Record<string, unknown> => {
  if (
    !isRecord(value) ||
    typeof value.sourceMessageId !== 'string' ||
    typeof value.threadId !== 'string' ||
    typeof value.parentMessageId !== 'string' ||
    typeof value.publisherId !== 'string'
  ) {
    return false;
  }

  return true;
};

const hasTelemetryContext = (value: unknown): value is ReplyPublisherPostedContext =>
  isRecord(value) &&
  value.postType === 'TeamsMeetingReply' &&
  typeof value.publisherInstanceId === 'string' &&
  typeof value.feedLocation === 'string' &&
  typeof value.isAnnouncement === 'boolean' &&
  Array.isArray(value.attachments) &&
  isRecord(value.rawContentState) &&
  Array.isArray(value.rawContentState.blocks) &&
  isRecord(value.rawContentState.entityMap);

const getStorageKey = (state: YammerState, messageId: Message['id']): string =>
  `${storagePrefix}${JSON.stringify([getCurrentNetworkId(state), getCurrentUserFieldThrows(state, 'id'), messageId])}`;

const getUnconfirmedReplacement = (
  replacement: ReplacementContext,
  clientMutationId: string
): UnconfirmedReplacement => ({
  stage: 'reconciliation',
  sourceMessageId: replacement.sourceMessageId,
  threadId: replacement.threadId,
  parentMessageId: replacement.parentMessageId,
  publisherId: replacement.publisherId,
  clientMutationId,
});

const notifyChanged = () => {
  listeners.forEach((notify) => {
    notify();
  });
};

export const subscribeToModeratedEventAgentReplyReplacements = (notify: () => void): (() => void) => {
  listeners.add(notify);

  return () => {
    listeners.delete(notify);
  };
};

const cancelExpiry = (key: string) => {
  clearTimeout(expiryTimers.get(key));
  expiryTimers.delete(key);
};

const redactUnconfirmedReplacement = (key: string, replacement: UnconfirmedReplacement): void => {
  removeSessionItem(key);
  cancelExpiry(key);
  setItemOrThrow(key, replacement);
  notifyChanged();
};

const scheduleExpiry = (key: string, replacement: StoredPublishingReplacement) => {
  if (expiryTimers.has(key)) {
    return;
  }
  const timer = setTimeout(
    () => {
      expiryTimers.delete(key);
      try {
        removeSessionItem(key);
        const current = getItem<unknown>(key);
        if (
          hasReplacementContext(current) &&
          current.stage === 'publishing' &&
          getStoredMutationId(current) === replacement.clientMutationId
        ) {
          redactUnconfirmedReplacement(key, getUnconfirmedReplacement(replacement, replacement.clientMutationId));
        }
      } catch (error) {
        reportError({ error });
        notifyChanged();
      }
    },
    Math.max(0, replacement.createdAt + replacementContentRetentionMilliseconds - Date.now())
  );
  expiryTimers.set(key, timer);
};

const hasExpired = (createdAt: unknown): boolean =>
  typeof createdAt !== 'number' ||
  !Number.isFinite(createdAt) ||
  createdAt > Date.now() ||
  Date.now() - createdAt >= replacementContentRetentionMilliseconds;

const getPublishingMetadata = (replacement: PublishingReplacement): StoredPublishingReplacement => ({
  stage: 'publishing',
  sourceMessageId: replacement.sourceMessageId,
  threadId: replacement.threadId,
  parentMessageId: replacement.parentMessageId,
  publisherId: replacement.publisherId,
  clientMutationId: replacement.clientMutationId,
  createdAt: replacement.createdAt,
});

const getStoredMutationId = (value: ReplacementContext & Record<string, unknown>): string => {
  if (typeof value.clientMutationId === 'string' && value.clientMutationId.length > 0) {
    return value.clientMutationId;
  }

  if (
    isRecord(value.variables) &&
    typeof value.variables.clientMutationId === 'string' &&
    value.variables.clientMutationId.length > 0
  ) {
    return value.variables.clientMutationId;
  }

  throw new Error('The saved Facilitator reply mutation identity is invalid');
};

export const pruneModeratedEventAgentReplyReplacements = (state: YammerState): void => {
  const keys = getKeys().filter((candidate) => candidate.startsWith(storagePrefix));
  const sessionKeys = getSessionKeys().filter((candidate) => candidate.startsWith(storagePrefix));
  for (const key of new Set([...expiryTimers.keys(), ...sessionKeys])) {
    if (!keys.includes(key)) {
      removeSessionItem(key);
      cancelExpiry(key);
    }
  }
  const ownerPrefix = `${storagePrefix}${JSON.stringify([
    getCurrentNetworkId(state),
    getCurrentSessionUserField(state, 'id'),
  ]).slice(0, -1)},`;
  for (const key of keys) {
    const value = getItem<unknown>(key);
    if (!hasReplacementContext(value)) {
      throw new Error('The saved Facilitator reply replacement is invalid');
    }

    const payload = getSessionItem<unknown>(key);
    const hasOwnedPublishingPayload =
      value.stage === 'publishing' && isRecord(payload) && payload.clientMutationId === getStoredMutationId(value);
    const hasOwningAccountChanged = hasOwnedPublishingPayload && !key.startsWith(ownerPrefix);

    if (value.stage === 'publishing' && (hasOwningAccountChanged || hasExpired(value.createdAt))) {
      redactUnconfirmedReplacement(key, getUnconfirmedReplacement(value, getStoredMutationId(value)));
    } else if (value.stage === 'publishing' && typeof value.createdAt === 'number' && hasOwnedPublishingPayload) {
      scheduleExpiry(key, {
        ...getUnconfirmedReplacement(value, getStoredMutationId(value)),
        stage: 'publishing',
        createdAt: value.createdAt,
      });
    } else {
      removeSessionItem(key);
      cancelExpiry(key);
    }
  }
};

const hasMatchingContext = (replacement: ReplacementContext, stored: ReplacementContext): boolean =>
  replacement.sourceMessageId === stored.sourceMessageId &&
  replacement.publisherId === stored.publisherId &&
  replacement.threadId === stored.threadId &&
  replacement.parentMessageId === stored.parentMessageId;

export const getModeratedEventAgentReplyReplacement = (
  state: YammerState,
  messageId: Message['id']
): ModeratedEventAgentReplyReplacement | undefined => {
  const key = getStorageKey(state, messageId);
  const value = getItem<unknown>(key);
  if (Object.is(value, null)) {
    return undefined;
  }

  if (!hasReplacementContext(value) || value.sourceMessageId !== messageId) {
    throw new Error('The saved Facilitator reply replacement is invalid');
  }

  if (
    value.stage === 'cleanup' &&
    typeof value.publishedMessageId === 'string' &&
    value.publishedMessageId.length > 0
  ) {
    return {
      stage: 'cleanup',
      sourceMessageId: value.sourceMessageId,
      threadId: value.threadId,
      parentMessageId: value.parentMessageId,
      publisherId: value.publisherId,
      publishedMessageId: value.publishedMessageId,
      clientMutationId: typeof value.clientMutationId === 'string' ? value.clientMutationId : undefined,
    };
  }
  const clientMutationId = getStoredMutationId(value);
  if (value.stage === 'reconciliation') {
    return getUnconfirmedReplacement(value, clientMutationId);
  }

  if (value.stage !== 'publishing') {
    throw new Error('The saved Facilitator reply replacement stage is invalid');
  }

  if (hasExpired(value.createdAt)) {
    return getUnconfirmedReplacement(value, clientMutationId);
  }
  const payload = getSessionItem<unknown>(key);
  if (payload == null) {
    return getUnconfirmedReplacement(value, clientMutationId);
  }

  if (
    !hasReplacementContext(payload) ||
    !hasPublishingRequest(payload) ||
    payload.variables.clientMutationId !== clientMutationId ||
    payload.clientMutationId !== clientMutationId ||
    payload.createdAt !== value.createdAt ||
    !hasMatchingContext(payload, value)
  ) {
    throw new Error('The saved Facilitator reply payload is invalid');
  }

  return payload;
};

export const saveModeratedEventAgentReplyReplacement = (
  state: YammerState,
  replacement: ModeratedEventAgentReplyReplacement
): void => {
  const key = getStorageKey(state, replacement.sourceMessageId);
  if (replacement.stage === 'publishing') {
    const metadata = getPublishingMetadata(replacement);
    setSessionItemOrThrow(key, replacement);
    try {
      setItemOrThrow(key, metadata);
    } catch (error) {
      removeSessionItem(key);
      throw error;
    }
    scheduleExpiry(key, metadata);
  } else {
    setItemOrThrow(key, replacement);
    removeSessionItem(key);
    cancelExpiry(key);
  }
  notifyChanged();
};

export const clearModeratedEventAgentReplyReplacement = (state: YammerState, messageId: Message['id']): void => {
  const key = getStorageKey(state, messageId);
  removeSessionItem(key);
  removeItem(key);
  cancelExpiry(key);
  notifyChanged();
};
